import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/heroSlides";
import { films } from "@/lib/films";

export default function HeroSection() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef<number | null>(null);
  const total = heroSlides.length;
  const slide = heroSlides[index];

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, total]);

  const goTo = (next: number) => setIndex((next + total) % total);

  const topFilms = [...films].sort((a, b) => b.rating - a.rating).slice(0, 5);

  return (
    <section className="container-portal pt-7 md:pt-10">
      <div className="flex flex-col gap-3 border-b border-background-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-[26px] font-black leading-tight tracking-tight text-foreground-950 md:text-[34px]">
            人兽影视网
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            电影与电视剧的资讯、剧情与影评，一处看清。
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-foreground-500">
          <span className="hidden sm:inline">2026 年 10 月 17 日</span>
          <span className="hidden h-3 w-px bg-background-300 sm:inline-block" />
          <span className="flex items-center gap-1.5 rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-medium text-accent-800">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
            内容演示站点
          </span>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.9fr)_minmax(0,1fr)]">
        <div
          className="relative h-[420px] overflow-hidden rounded-lg bg-secondary-900 md:h-[500px] lg:h-[540px]"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={(event) => {
            touchStart.current = event.touches[0].clientX;
          }}
          onTouchEnd={(event) => {
            if (touchStart.current === null) return;
            const delta = event.changedTouches[0].clientX - touchStart.current;
            if (Math.abs(delta) > 45) {
              goTo(delta > 0 ? index - 1 : index + 1);
            }
            touchStart.current = null;
          }}
        >
          {heroSlides.map((item, itemIndex) => (
            <div
              key={item.id}
              className={[
                "absolute inset-0 transition-opacity duration-700",
                itemIndex === index ? "opacity-100" : "pointer-events-none opacity-0",
              ].join(" ")}
            >
              <img
                src={item.image}
                alt={`${item.title} 剧照`}
                title={`${item.title} 影视资讯`}
                className={[
                  "h-full w-full object-cover object-center",
                  itemIndex === index ? "animate-slow-zoom" : "",
                ].join(" ")}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/92 via-foreground-950/45 to-foreground-950/15" />
            </div>
          ))}

          <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
            <div className="max-w-xl">
              <span className="label-caps inline-flex items-center gap-2 text-[11px] text-accent-300">
                <span className="h-[2px] w-5 bg-accent-400" />
                {slide.kicker}
              </span>
              <h2 className="mt-3 text-2xl font-black leading-tight text-background-50 md:text-[34px]">
                {slide.title}
              </h2>
              <p className="mt-2 line-clamp-2 text-[13px] leading-6 text-background-100/85 md:text-sm">
                {slide.description}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${slide.filmId}`}
                  className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap cursor-pointer"
                >
                  查看详情
                  <i className="ri-arrow-right-line" />
                </Link>
                <span className="text-[11px] text-background-200/80">{slide.meta}</span>
              </div>
            </div>
          </div>

          <div className="absolute right-4 top-4 flex items-center gap-2">
            <button
              type="button"
              onClick={() => goTo(index - 1)}
              aria-label="上一张"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-background-50/30 bg-foreground-950/20 text-background-50 backdrop-blur-sm transition-colors hover:bg-foreground-950/50 cursor-pointer"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            <button
              type="button"
              onClick={() => goTo(index + 1)}
              aria-label="下一张"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-background-50/30 bg-foreground-950/20 text-background-50 backdrop-blur-sm transition-colors hover:bg-foreground-950/50 cursor-pointer"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>

          <div className="absolute bottom-5 right-4 hidden items-center gap-1.5 md:flex">
            {heroSlides.map((item, itemIndex) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setIndex(itemIndex)}
                aria-label={`切换到第 ${itemIndex + 1} 张`}
                className={[
                  "h-1 rounded-full transition-all cursor-pointer",
                  itemIndex === index ? "w-6 bg-accent-500" : "w-2 bg-background-50/50",
                ].join(" ")}
              />
            ))}
          </div>
        </div>

        <aside className="flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="flex items-center justify-between border-b border-background-200 px-4 py-3.5">
            <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
              <i className="ri-fire-line text-accent-500" />
              站内评分榜
            </h2>
            <span className="text-[11px] text-foreground-500">实时更新</span>
          </div>
          <ul className="flex-1 divide-y divide-background-200/70">
            {topFilms.map((film, rank) => (
              <li key={film.id}>
                <Link
                  to={`/detail/${film.id}`}
                  className="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-background-100 cursor-pointer"
                >
                  <span
                    className={[
                      "font-label text-lg leading-none w-6",
                      rank < 3 ? "text-accent-500" : "text-foreground-400",
                    ].join(" ")}
                  >
                    {String(rank + 1).padStart(2, "0")}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13.5px] font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                      {film.title}
                    </span>
                    <span className="mt-0.5 block truncate text-[11px] text-foreground-500">
                      {film.year} · {film.genres.join(" / ")}
                    </span>
                  </span>
                  <span className="flex items-center gap-1 text-xs font-semibold text-accent-600">
                    <i className="ri-star-fill text-[11px]" />
                    {film.rating.toFixed(1)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to="/special/silent-current"
            className="group block border-t border-background-200 bg-accent-50 px-4 py-3 transition-colors hover:bg-accent-100 cursor-pointer"
          >
            <span className="label-caps text-[10px] text-accent-700">专题 · 第 12 期</span>
            <span className="mt-1 flex items-center justify-between gap-2 text-[12.5px] font-medium leading-5 text-accent-900">
              静水深流：华语悬疑的「慢」与「狠」
              <i className="ri-arrow-right-up-line shrink-0 text-accent-600 transition-transform group-hover:-translate-y-0.5" />
            </span>
          </Link>
          <Link
            to="/category/movie"
            className="flex items-center justify-between border-t border-background-200 bg-background-100 px-4 py-3 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
          >
            浏览完整榜单
            <i className="ri-arrow-right-line" />
          </Link>
        </aside>
      </div>
    </section>
  );
}